export type PrivacyTier = 'anon' | 'private' | 'tee';

export interface PrivacyMeta {
  /** Known tier (drives badge styling); null for an unrecognized label. */
  readonly tier: PrivacyTier | null;
  readonly label: string;
  /** One-line tooltip copy; empty for an unrecognized label. */
  readonly definition: string;
}

const META: Record<PrivacyTier, PrivacyMeta> = {
  tee: {
    tier: 'tee',
    label: 'TEE',
    definition:
      'Sealed inside verified secure hardware, so not even the host can read what you send.',
  },
  private: {
    tier: 'private',
    label: 'Private',
    definition:
      'Nothing you send or receive is kept, by contract with the provider.',
  },
  anon: {
    tier: 'anon',
    label: 'Anon',
    definition:
      'Requests are not linked to you, but the provider may keep what you send.',
  },
};

const PRIVACY_TAG_RE = /^privacy:\s*(.+)$/iu;

/**
 * Privacy badge for a model, read from the gateway's admin-curated catalog
 * tags (`Privacy: <label>`, matched case-insensitively; the first one wins).
 * Known labels (TEE / Private / Anon) carry their tooltip definitions and a
 * tier for badge styling; an unrecognized label still renders verbatim
 * (untiered, no tooltip) so a new tier ships without a UI release. No
 * Privacy tag ⇒ no badge — including the whole catalog when the gateway
 * served a fallback source that carries no tags.
 */
export function privacyFromTags(
  tags: readonly string[] | undefined,
): PrivacyMeta | null {
  if (tags == null) return null;
  for (const tag of tags) {
    const match = PRIVACY_TAG_RE.exec(tag.trim());
    if (match == null) continue;
    const label = match[1]!.trim();
    const key = label.toLowerCase();
    if (key === 'tee' || key === 'private' || key === 'anon') {
      return META[key];
    }
    return { tier: null, label, definition: '' };
  }
  return null;
}
